import {type ReactNode} from 'react'
import {useQuery} from '@tanstack/react-query'
import {routes} from '@/lib/routes'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {MutedText} from '@/components/shared/MutedText'
import {EmptyState} from '@/components/shared/EmptyState'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {ErrorBanner} from '@/components/ui/error-banner'
import {PageLayout} from '@/components/shared/PageLayout'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {formatDateOnly, formatTimestamp} from '@/lib/date-utils'
import {RehireReviewStatus, RehireDecisionActorType, type RehireReviewCandidate} from '@/types/rehire-review'
import {RehireReviewControllerListEndpoint} from '@/types/endpoint-contracts'

const STATUS_VARIANT: Record<RehireReviewStatus, BadgeVariant> = {
    PENDING: BadgeVariant.OUTLINE,
    RESURRECTED: BadgeVariant.SUCCESS,
    CREATED_NEW: BadgeVariant.SECONDARY,
    REJECTED: BadgeVariant.DESTRUCTIVE,
}

function renderDecidedBy(
    candidate: RehireReviewCandidate,
    i18n: ReturnType<typeof useI18n>,
): ReactNode {
    if (candidate.decidedByActorType === RehireDecisionActorType.CONNECTOR && candidate.decidedByActorId) {
        const label = i18n.rehireReview.decidedByConnector({
            name: candidate.decidedByConnectorName ?? candidate.decidedByActorId,
        })
        return (
            <Badge variant={BadgeVariant.SECONDARY} data-testid="badge-decided-by-connector">
                {label}
            </Badge>
        )
    }
    if (candidate.decidedByActorType === RehireDecisionActorType.USER && candidate.decidedByActorId) {
        return <MutedText>{candidate.decidedByActorId}</MutedText>
    }
    return <MutedText>{'\u2014'}</MutedText>
}

export function RehireReview() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.rehireReview.pageTitle)

    const {
        page, pageSize, queryString,
        searchAndFilters, setSearchAndFilters,
        setPage, setPageSize, pageSizeOptions,
    } = useServerPagination({
        endpoint: RehireReviewControllerListEndpoint,
        // Backend uses @SortableFields({}) — server applies fixed ordering (detectedAt DESC)
        defaultSortBy: '',
        defaultSortDir: SortDir.DESC,
    })

    const {data, isLoading, error} = useQuery({
        queryKey: ['rehire-review-mine', queryString],
        queryFn: () => services.rehireReview.listMine(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const totalItems = data?.totalElements ?? 0
    const totalPages = data?.totalPages ?? 0
    const candidates: RehireReviewCandidate[] = data?.content ?? []

    const renderStatusBadge = (status: RehireReviewStatus) => {
        const label =
            status === RehireReviewStatus.PENDING ? i18n.rehireReview.status.pending :
            status === RehireReviewStatus.RESURRECTED ? i18n.rehireReview.status.resurrected :
            status === RehireReviewStatus.CREATED_NEW ? i18n.rehireReview.status.createdNew :
            i18n.rehireReview.status.rejected
        return <Badge variant={STATUS_VARIANT[status]}>{label}</Badge>
    }

    return (
        <>
            <ErrorBanner error={error}/>
            <PageLayout
                variant="list"
                icon={CONCEPT_ICON.rehireReview}
                title={i18n.rehireReview.title}
                description={i18n.rehireReview.subtitle}
                count={totalItems}
                data-testid="rehire-review-list"
                filters={
                    <UnifiedSearchBar
                        contract={RehireReviewControllerListEndpoint}
                        value={searchAndFilters}
                        onChange={setSearchAndFilters}
                        placeholder={i18n.rehireReview.filters.status as string}
                    />
                }
                emptyState={!isLoading && candidates.length === 0 ? (
                    <EmptyState
                        title={i18n.rehireReview.emptyState.title}
                        description={i18n.rehireReview.emptyState.description}
                        data-testid="rehire-review-empty"
                    />
                ) : undefined}
                pagination={totalItems > 0 ? (
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        totalItems={totalItems}
                        totalPages={totalPages}
                        onPageChange={setPage}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={pageSizeOptions}
                    />
                ) : undefined}
            >
                {isLoading ? (
                    <MutedText block centered data-testid="rehire-review-loading">{i18n.common.loading}</MutedText>
                ) : candidates.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-matched-identity">{i18n.rehireReview.columns.matchedIdentity}</TableHead>
                                <TableHead data-testid="col-source-connector">{i18n.rehireReview.columns.sourceConnector}</TableHead>
                                <TableHead data-testid="col-new-entry-date">{i18n.rehireReview.columns.newEntryDate}</TableHead>
                                <TableHead data-testid="col-detected-at">{i18n.rehireReview.columns.detectedAt}</TableHead>
                                <TableHead data-testid="col-status">{i18n.rehireReview.columns.status}</TableHead>
                                <TableHead data-testid="col-decided-by">{i18n.rehireReview.columns.decidedBy}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {candidates.map(candidate => (
                                <ClickableTableRow
                                    key={candidate.id}
                                    data-testid={`rehire-review-row-${candidate.id}`}
                                    to={routes.my.rehires.detail(candidate.id)}
                                >
                                    <TableCell>{candidate.matchedIdentityDisplayName ?? candidate.matchedIdentityId}</TableCell>
                                    <TableCell>{candidate.sourceConnectorDisplayName ?? candidate.sourceConnectorId}</TableCell>
                                    <TableCell>{formatDateOnly(candidate.newEntryDate)}</TableCell>
                                    <TableCell>{formatTimestamp(candidate.detectedAt)}</TableCell>
                                    <TableCell>{renderStatusBadge(candidate.status)}</TableCell>
                                    <TableCell data-testid={`cell-decided-by-${candidate.id}`}>
                                        {renderDecidedBy(candidate, i18n)}
                                    </TableCell>
                                </ClickableTableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : null}
            </PageLayout>
        </>
    )
}
